import styled, { CSSObject } from '@emotion/styled'
import { InputGroup } from '../Controls'
import { UseFormReturn } from 'react-hook-form'
import { CheckoutForm, FormFields } from './types'
import AlertMessage from '../shared/AlertMessage/AlertMessage'
import {
  focusNextInput,
  handlePhoneNumberOnChange,
  handlePhoneNumberOnBlur,
  isInternationalNumber
} from './utils'
import useInputKeyEnterHandler from 'shop/hooks/useInputKeyEnterHandler'
import FormInput from '../Inputs/FormInput'
import { HalfWidthFlexContainer } from './commonStyles'
import { emailRegex } from 'shop/utils/common'
import { IntlPhoneNumber } from '../Inputs'
import { useCheckout, useConsumerCart, useMediaQueries } from 'shop/hooks'
import React, { useEffect, useRef, useState } from 'react'
import { strippedContactNumPrefix } from '../Inputs/utils'
import { AccountIcon } from 'shop/assets/icons'
import ExpandableDiv from '../ExpandableContainer/ExpandableDiv'
import CollapsedPersonDetails from './CollapsedPersonDetails'
import GenericBreakdownItem from '../GenericBreakdownItem/GenericBreakdownItem'
import { scrollToElement } from '../Controls/utils'
import { handleFormFieldCartUpdate } from './formCartUpdates'
import { useFocusOutListener } from 'shop/hooks/useFocusOutListener'
import { DELIVERY_FULFILLMENT } from 'shop/types'

const CUSTOMER_PREFIX = 'customerDetails'

export const CUSTOMER_INPUTS = {
  firstNameInputId: `${CUSTOMER_PREFIX}[firstName]`,
  lastNameInputId: `${CUSTOMER_PREFIX}[lastName]`,
  emailInputId: `${CUSTOMER_PREFIX}[email]`,
  phonePrefixId: `${CUSTOMER_PREFIX}[contactNumPrefix]`,
  phoneInputId: `${CUSTOMER_PREFIX}[contactNumber]`
}

type Props = {
  formHandle: UseFormReturn<FormFields>
  onBlur: () => void
  animationsReady: boolean
  prevCustomerDetailsRef: React.MutableRefObject<
    CheckoutForm['customerDetails'] | undefined
  >
}

const CustomerDetailsForm = ({
  formHandle,
  onBlur,
  animationsReady,
  prevCustomerDetailsRef
}: Props) => {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
    trigger
  } = formHandle
  const detailsErrors = errors['customerDetails']
  const formInitialised = watch('__formInitialised')
  const forSomeoneElse = watch('forSomeoneElse')

  const [isPersonalDetailsExpanded, setIsPersonalDetailsExpanded] =
    useState<boolean>(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  const { trackAddShippingInfo } = useCheckout()
  const { isMobile } = useMediaQueries()
  const { updateConsumerCart, cart } = useConsumerCart()

  const {
    firstNameInputId,
    lastNameInputId,
    emailInputId,
    phonePrefixId,
    phoneInputId
  } = CUSTOMER_INPUTS

  const customerDetails = watch('customerDetails')

  const { firstName, lastName, email, contactNumber, contactNumPrefix } =
    customerDetails
  const hasAllDetails = !!firstName && !!lastName && !!email && !!contactNumber

  const handleBlur = () => {
    onBlur()
    // sending tracking event, only if all fields are filled
    if (hasAllDetails) {
      trackAddShippingInfo()
    }
  }

  useInputKeyEnterHandler({
    handleKeyPress: () =>
      focusNextInput([
        firstNameInputId,
        lastNameInputId,
        emailInputId,
        phoneInputId
      ])
  })

  const handleEmailInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = e.target.value.toLowerCase()
  }

  const handlePhoneInputsBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    handlePhoneNumberOnBlur(
      e,
      setValue,
      trigger,
      'customerDetails.contactNumber'
    )
    handleBlur()
  }

  // Expand the form on load if incomplete or with errors
  useEffect(() => {
    if ((detailsErrors || !hasAllDetails) && formInitialised) {
      setIsPersonalDetailsExpanded(true)
    }
  }, [detailsErrors, hasAllDetails, formInitialised])

  // Set listener for when the form section loses focus to trigger update cart and collapse.
  const handleFocusOut = async (e: FocusEvent) => {
    handleFormFieldCartUpdate({
      formRef: sectionRef,
      checkoutForm: formHandle,
      event: e,
      prevFieldValue: prevCustomerDetailsRef,
      formFieldName: 'customerDetails',
      updateConsumerCart,
      handleCloseSection: () => setIsPersonalDetailsExpanded(false)
    })
  }
  useFocusOutListener({ sectionRef, onFocusOut: handleFocusOut })

  const handleChangeButtonClick = () => {
    setIsPersonalDetailsExpanded(true)
    if (isMobile) {
      // scroll to section when toggling it on for Mobile
      // timeout for collapsiing section animations
      setTimeout(() => {
        scrollToElement('details-section', 80)
      }, 400)
    }
  }

  if (!formInitialised) return <></>

  return (
    <div>
      {detailsErrors && (
        <AlertMessageWrapper>
          <AlertMessage
            skin='mild'
            type='error'
            heading={Object.values(detailsErrors as object)[0]?.message || ''}
          />
        </AlertMessageWrapper>
      )}
      <ExpandableDiv
        isExpanded={!isPersonalDetailsExpanded && hasAllDetails}
        collapsedHeight={0}
        transition={animationsReady ? undefined : 'none'}
      >
        <CollapsedDetailsContainer>
          <GenericBreakdownItem
            label={
              <CollapsedPersonDetails
                firstName={customerDetails.firstName}
                lastName={customerDetails.lastName}
                email={customerDetails.email}
                contactNumber={`${strippedContactNumPrefix(customerDetails.contactNumPrefix)}${customerDetails.contactNumber}`}
              />
            }
            maxWidth={'100%'}
            withEllipsis={false}
            withLineBreak={false}
            LeftIcon={StyledAccountIcon}
            padding={'0'}
            onChangeButtonClick={handleChangeButtonClick}
          />
        </CollapsedDetailsContainer>
      </ExpandableDiv>
      <ExpandableDiv
        isExpanded={isPersonalDetailsExpanded}
        collapsedHeight={0}
        transition={animationsReady ? undefined : 'none'}
        id='customer-details-expandable-div'
      >
        <FieldsContainer ref={sectionRef}>
          <InputGroup templateColumns='1fr' gridGap='18px'>
            <HalfWidthFlexContainer>
              <FormInput
                id={firstNameInputId}
                labelText='First Name'
                name={firstNameInputId}
                autoComplete='given-name'
                value={customerDetails?.firstName || ''}
                onBlur={handleBlur}
                showError={!!errors?.customerDetails?.firstName}
                formRef={register('customerDetails.firstName', {
                  required: 'Please enter your first name',
                  validate: (value: string) =>
                    value.trim().length > 0
                      ? true
                      : 'Please enter your first name'
                })}
                scrollSettings={{
                  scrollOnFocus: true,
                  scrollToElementId: 'details-section'
                }}
                tabIndex={!isPersonalDetailsExpanded ? -1 : 0}
              />
              <FormInput
                id={lastNameInputId}
                name={lastNameInputId}
                value={customerDetails?.lastName || ''}
                labelText='Last Name'
                autoComplete='family-name'
                onBlur={handleBlur}
                showError={!!errors?.customerDetails?.lastName}
                formRef={register('customerDetails.lastName', {
                  required: 'Please enter your last name',
                  validate: (value: string) =>
                    value.trim().length > 0
                      ? true
                      : 'Please enter your last name'
                })}
                scrollSettings={{
                  scrollOnFocus: true,
                  scrollToElementId: 'details-section'
                }}
                tabIndex={!isPersonalDetailsExpanded ? -1 : 0}
              />
            </HalfWidthFlexContainer>
            <FormInput
              id={emailInputId}
              name={emailInputId}
              labelText='Email Address'
              value={customerDetails?.email || ''}
              autoComplete='email'
              onBlur={handleBlur}
              onChange={handleEmailInputChange}
              showError={!!errors?.customerDetails?.email}
              inputMode='email'
              formRef={register('customerDetails.email', {
                required: 'Please enter your email address',
                pattern: {
                  value: emailRegex,
                  message: 'Invalid email address'
                }
              })}
              scrollSettings={{
                scrollOnFocus: true,
                scrollToElementId: 'details-section'
              }}
              tabIndex={!isPersonalDetailsExpanded ? -1 : 0}
            />
            <IntlPhoneNumber
              onNumberChange={handlePhoneNumberOnChange}
              onBlur={handlePhoneInputsBlur}
              formHandle={formHandle}
              prefixDropdownId={phonePrefixId}
              labelText='Phone Number'
              prefixDropdownValue={customerDetails?.contactNumPrefix}
              prefixDropdownName={'customerDetails.contactNumPrefix'}
              phoneNumberInputName={'customerDetails.contactNumber'}
              phoneNumberInputId={phoneInputId}
              phoneNumberValue={customerDetails?.contactNumber || ''}
              showError={
                !!errors?.customerDetails?.contactNumber ||
                !!errors?.customerDetails?.contactNumPrefix
              }
              scrollSettings={{
                scrollOnFocus: true,
                scrollToElementId: 'details-section'
              }}
              forceCloseDropdown={!isPersonalDetailsExpanded}
              tabIndex={!isPersonalDetailsExpanded ? -1 : 0}
              expandableContainerId={'customer-details-expandable-div'}
            />
          </InputGroup>
        </FieldsContainer>
      </ExpandableDiv>
      {!forSomeoneElse &&
        cart?.fulfillment?.type === DELIVERY_FULFILLMENT &&
        isInternationalNumber(contactNumPrefix) && (
          <AlertMessageWrapper>
            <AlertMessage
              skin='warning'
              type='warning'
              subheading='Delivery couriers may not be able to contact international numbers. You may miss delivery updates and calls.'
            />
          </AlertMessageWrapper>
        )}
    </div>
  )
}

const AlertMessageWrapper = styled.div(() => ({
  margin: '16px 0'
}))

const FieldsContainer = styled.div(
  (): CSSObject => ({
    display: 'block',
    padding: '8px 0 0'
  })
)

const CollapsedDetailsContainer = styled.div(
  (): CSSObject => ({
    marginBottom: '8px'
  })
)

const StyledAccountIcon = styled(AccountIcon)(() => ({
  '>path': {
    fill: '#000'
  }
}))

export default CustomerDetailsForm
